import { z } from 'zod'
import { isPostCodeRequiredForCountry } from '@/shared/config/countries'
import {
  createOptionalLatinCharacterSchema,
  createRequiredLatinCharacterSchema
} from '@/shared/lib/latinCharacterValidation'

const streetLatinMsg = 'create.validation.streetLatin'
const streetNumberLatinMsg = 'create.validation.streetNumberLatin'
const postCodeLatinMsg = 'create.validation.postCodeLatin'
const poBoxLatinMsg = 'create.validation.poBoxLatin'
const cityLatinMsg = 'create.validation.cityLatin'
const postCodeRequiredMsg = 'create.validation.postCodeRequired'

function preprocessInstallationCoordinate(value: unknown): number {
  if (value === '' || value === null || value === undefined) {
    return 0
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }
  if (typeof value === 'string' && value.trim() === '') {
    return 0
  }
  const parsed =
    typeof value === 'string' ? Number.parseFloat(value) : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function refinePostCodeWhenRequired(
  data: { country: string; postCode?: string },
  context: z.RefinementCtx,
  path: 'postCode' | 'localPostCode',
  postCodeValue: string | undefined
) {
  if (!isPostCodeRequiredForCountry(data.country)) {
    return
  }
  if (!(postCodeValue ?? '').trim()) {
    context.addIssue({
      code: 'custom',
      message: postCodeRequiredMsg,
      path: [path]
    })
  }
}

export const installationGoodsFamilySchema = z.enum([
  'ALUMINIUM',
  'CEMENT',
  'IRONANDSTEEL',
  'ELECTRICITY',
  'CHEMICALS',
  'FERTILIZERS'
])

export type InstallationGoodsFamily = z.infer<typeof installationGoodsFamilySchema>

export function createInstallationCreateStep1Schema(options: {
  requiresLocalNameAndAddress: boolean
}) {
  return z
    .object({
      name: createRequiredLatinCharacterSchema('create.validation.nameLatin', {
        min: 2
      }),
      localName: z.union([z.string(), z.literal('')]).optional(),
      goodsFamily: z
        .string()
        .min(1, { message: 'create.validation.goodsFamilyRequired' })
        .pipe(installationGoodsFamilySchema),
      description: z
        .string()
        .max(10_000)
        .refine((value) => value.trim().length > 0, {
          message: 'create.validation.descriptionRequired',
        }),
    })
    .superRefine((data, context) => {
      if (options.requiresLocalNameAndAddress) {
        const trimmedLocalName = (data.localName ?? '').trim()
        if (trimmedLocalName.length < 1) {
          context.addIssue({
            code: 'custom',
            message: 'create.validation.localNameRequired',
            path: ['localName']
          })
        }
      }
    })
}

export const installationLatinAddressSchema = z
  .object({
    street: createRequiredLatinCharacterSchema(streetLatinMsg, { max: 70 }),
    street2: createOptionalLatinCharacterSchema(streetLatinMsg, { max: 70 }),
    streetNumber: createRequiredLatinCharacterSchema(streetNumberLatinMsg, {
      max: 35
    }),
    country: z.string().length(2),
    postCode: createOptionalLatinCharacterSchema(postCodeLatinMsg, { max: 17 }),
    poBox: createOptionalLatinCharacterSchema(poBoxLatinMsg, { max: 70 }),
    city: createRequiredLatinCharacterSchema(cityLatinMsg, { min: 2, max: 35 }),
    unlocode: z.union([z.string().max(17), z.literal('')]).optional(),
    longitude: z.preprocess(
      preprocessInstallationCoordinate,
      z
        .number()
        .min(-180, { message: 'create.validation.longitudeRange' })
        .max(180, { message: 'create.validation.longitudeRange' })
    ),
    latitude: z.preprocess(
      preprocessInstallationCoordinate,
      z
        .number()
        .min(-90, { message: 'create.validation.latitudeRange' })
        .max(90, { message: 'create.validation.latitudeRange' })
    )
  })
  .superRefine((data, context) => {
    refinePostCodeWhenRequired(data, context, 'postCode', data.postCode)
  })

export const installationLocalAddressSchema = z
  .object({
    country: z.string().length(2),
    localStreet: z.string().min(1).max(70),
    localStreet2: z.string().max(70).optional().or(z.literal('')),
    localStreetNumber: z.string().min(1).max(35),
    localPostCode: z.string().max(17).optional().or(z.literal('')),
    localPoBox: z.string().max(70).optional().or(z.literal('')),
    localCity: z.string().min(2).max(35)
  })
  .superRefine((data, context) => {
    refinePostCodeWhenRequired(
      data,
      context,
      'localPostCode',
      data.localPostCode
    )
  })

export function createInstallationCreateFormSchema(options: {
  requiresLocalNameAndAddress: boolean
}) {
  return z
    .object({
      name: createRequiredLatinCharacterSchema('create.validation.nameLatin', {
        min: 2
      }),
      localName: z.union([z.string(), z.literal('')]).optional(),
      goodsFamily: z
        .string()
        .min(1, { message: 'create.validation.goodsFamilyRequired' })
        .pipe(installationGoodsFamilySchema),
      description: z
        .string()
        .max(10_000)
        .refine((value) => value.trim().length > 0, {
          message: 'create.validation.descriptionRequired',
        }),
      ...installationLatinAddressSchema.shape,
      ...installationLocalAddressSchema.shape
    })
    .superRefine((data, context) => {
      if (options.requiresLocalNameAndAddress) {
        const trimmedLocalName = (data.localName ?? '').trim()
        if (trimmedLocalName.length < 1) {
          context.addIssue({
            code: 'custom',
            message: 'create.validation.localNameRequired',
            path: ['localName']
          })
        }
        refinePostCodeWhenRequired(
          data,
          context,
          'localPostCode',
          data.localPostCode
        )
      }
      refinePostCodeWhenRequired(data, context, 'postCode', data.postCode)
    })
}

export type InstallationCreateFormInput = z.input<
  ReturnType<typeof createInstallationCreateFormSchema>
>

export type InstallationCreateFormParsed = z.output<
  ReturnType<typeof createInstallationCreateFormSchema>
>

export type InstallationCreateApiPayload = {
  name: string
  localName?: string
  goodsFamily: InstallationGoodsFamily
  description: string
  street: string
  street2?: string
  streetNumber: string
  country: string
  postCode?: string
  poBox?: string
  city: string
  unlocode?: string
  longitude: number
  latitude: number
  localStreet: string
  localStreet2?: string
  localStreetNumber: string
  localPostCode?: string
  localPoBox?: string
  localCity: string
}

export function mapInstallationCreateFormToApiPayload(
  values: InstallationCreateFormParsed,
  options: { requiresLocalNameAndAddress: boolean }
): InstallationCreateApiPayload {
  const descriptionTrimmed = (values.description ?? '').trim()
  const base: InstallationCreateApiPayload = {
    name: values.name.trim(),
    goodsFamily: values.goodsFamily,
    description: descriptionTrimmed,
    street: values.street.trim(),
    street2: values.street2?.trim() || undefined,
    streetNumber: values.streetNumber.trim(),
    country: values.country.trim().toUpperCase(),
    postCode: values.postCode?.trim() || undefined,
    poBox: values.poBox?.trim() || undefined,
    city: values.city.trim(),
    unlocode: values.unlocode?.trim() || undefined,
    longitude: values.longitude,
    latitude: values.latitude,
    localStreet: values.localStreet.trim(),
    localStreet2: values.localStreet2?.trim() || undefined,
    localStreetNumber: values.localStreetNumber.trim(),
    localPostCode: values.localPostCode?.trim() || undefined,
    localPoBox: values.localPoBox?.trim() || undefined,
    localCity: values.localCity.trim()
  }

  if (options.requiresLocalNameAndAddress) {
    base.localName = (values.localName ?? '').trim()
  } else {
    base.localName = values.name.trim()
    base.localStreet = values.street.trim()
    base.localStreet2 = values.street2?.trim() || undefined
    base.localStreetNumber = values.streetNumber.trim()
    base.localPostCode = values.postCode?.trim() || undefined
    base.localPoBox = values.poBox?.trim() || undefined
    base.localCity = values.city.trim()
  }

  return base
}

export function getInstallationCreateFormDefaultValues(
  defaultCountryCode: string
): InstallationCreateFormInput {
  return {
    name: '',
    localName: '',
    goodsFamily: '',
    description: '',
    street: '',
    street2: '',
    streetNumber: '',
    country: defaultCountryCode,
    postCode: '',
    poBox: '',
    city: '',
    unlocode: '',
    longitude: 0,
    latitude: 0,
    localStreet: '',
    localStreet2: '',
    localStreetNumber: '',
    localPostCode: '',
    localPoBox: '',
    localCity: ''
  }
}
